"use client";

import { ChevronRight, MapPin } from "lucide-react";
import type { HiiveBookableNow } from "@/actions/venue-discovery";
import type { PublicVenueSummary } from "@/actions/venue-public";
import { AppImage as Image } from "@/components/ui/app-image";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { flattenBookableCards } from "@/features/hiives/components/bookable-now/bookable-now.shared";
import { BookableNowCards } from "@/features/hiives/components/bookable-now/bookable-now-cards.client";
import { firstMediaPreviewUrl } from "@/lib/media";

interface HiiveVenuesTabClientProps {
  hiiveSlug: string;
  initialVenues: PublicVenueSummary[];
  bookableNow: HiiveBookableNow;
}

export function HiiveVenuesTabClient({
  hiiveSlug,
  initialVenues,
  bookableNow,
}: HiiveVenuesTabClientProps) {
  const hasBookable = bookableNow.groups.length > 0;

  return (
    <>
      <BookableNowCards
        title="Find your next class"
        caption="Next 48 hours"
        className="px-2 pt-1 pb-4"
        cards={flattenBookableCards(bookableNow.groups)}
      />
      <section className="space-y-3 px-3 pb-24">
        {hasBookable ? (
          <h2 className="px-1 pt-1 text-base font-semibold text-text-primary dark:text-white">
            All venues
          </h2>
        ) : null}
        {initialVenues.map((venue) => {
          const imageUrl = firstMediaPreviewUrl(venue.bannerMedia);
          return (
            <BackAwareLink
              key={venue.id}
              href={`/hiives/${hiiveSlug}/venues/${venue.slug}`}
              // Empty prefetch, repeated per list row — see back-aware-link.tsx.
              prefetch={false}
              className="flex min-h-24 gap-3 rounded-lg border border-border bg-surface p-3 transition hover:bg-surface-alt dark:border-white/[0.1] dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
            >
              <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-surface-alt dark:bg-white/[0.06]">
                {imageUrl ? (
                  <Image
                    src={imageUrl}
                    alt={venue.name}
                    width={80}
                    height={80}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-text-tertiary dark:text-white/42">
                    <MapPin className="h-6 w-6" aria-hidden />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-text-primary dark:text-white">
                  {venue.name}
                </div>
                <div className="mt-1 line-clamp-2 text-sm text-text-secondary dark:text-white/58">
                  {venue.address}
                </div>
                <div className="mt-2 text-xs text-text-tertiary dark:text-white/42">
                  {venue.experienceCount} experience
                  {venue.experienceCount === 1 ? "" : "s"}
                </div>
              </div>
              <ChevronRight
                className="mt-1 h-5 w-5 shrink-0 text-text-tertiary dark:text-white/38"
                aria-hidden
              />
            </BackAwareLink>
          );
        })}
      </section>
    </>
  );
}
